<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import subWeeks from 'date-fns/subWeeks';
import BarChart from 'shared/components/charts/BarChart.vue';
import { bucketLabel } from '../../helper/crmBuckets';
import ReportMetricCard from '../ReportMetricCard.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import CrmDealsReportsAPI from 'dashboard/api/reports/crmDeals';
import Multiselect from 'vue-multiselect';

const { t } = useI18n();

// Estados
const customDateRange = ref([new Date(), new Date()]);
// Agregados calculados en el backend (V2::Reports::Crm::DealsBuilder), acotados al rango.
const summary = ref(null);
const isLoading = ref(false);

// Constantes de filtros de fecha
const DATE_FILTERS = {
  TODAY: 1,
  LAST_WEEK: 2,
  LAST_MONTH: 3,
  CUSTOM: 4
};

const dateFilterOptions = computed(() => [
  { id: DATE_FILTERS.TODAY, name: t('CRM_REPORTS.LEADS.FILTERS.TODAY') },
  { id: DATE_FILTERS.LAST_WEEK, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_WEEK') },
  { id: DATE_FILTERS.LAST_MONTH, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_MONTH') },
  { id: DATE_FILTERS.CUSTOM, name: t('CRM_REPORTS.LEADS.FILTERS.CUSTOM') }
]);

const selectedDateFilter = ref(dateFilterOptions.value[2]); // Default: Last Month

// Función para obtener todos los deals
const dateRange = computed(() => {
  if (!selectedDateFilter.value) return { start: new Date(), end: new Date() };
  
  const today = new Date();
  
  switch (selectedDateFilter.value.id) {
    case DATE_FILTERS.TODAY:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_WEEK:
      const lastWeekStart = subWeeks(today, 1);
      return {
        start: startOfWeek(lastWeekStart, { weekStartsOn: 1 }),
        end: endOfWeek(lastWeekStart, { weekStartsOn: 1 })
      };
    case DATE_FILTERS.LAST_MONTH:
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        start: startOfDay(lastMonthStart),
        end: endOfDay(lastMonthEnd)
      };
    case DATE_FILTERS.CUSTOM:
      return {
        start: startOfDay(customDateRange.value[0]),
        end: endOfDay(customDateRange.value[1])
      };
    default:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
  }
});

const fetchSummary = async () => {
  const { start, end } = dateRange.value;
  isLoading.value = true;
  try {
    const { data } = await CrmDealsReportsAPI.getSummary({
      since: Math.floor(start.getTime() / 1000),
      until: Math.floor(end.getTime() / 1000),
      sections: ['weighted_pipeline']
    });
    summary.value = data;
  } catch (error) {
    summary.value = null;
  } finally {
    isLoading.value = false;
  }
};

// El filtro ya tiene valor al montar, así que la primera carga la dispara `immediate`.
watch(dateRange, fetchSummary, { immediate: true });

// Pipeline ponderado = Σ(importe del negocio × probabilidad de su etapa), sobre los
// negocios vivos (status open/paused) creados dentro del rango.
const weightedPipeline = computed(
  () => summary.value?.weighted_pipeline || { ars: 0, usd: 0, total: 0 }
);

// Granularidad elegida por el backend según el largo del rango (día / semana / mes).
const granularity = computed(() => summary.value?.range?.granularity || 'day');
const labelFor = timestamp => bucketLabel(timestamp, granularity.value);

// Detalle por bucket (el backend elige día / semana / mes según el largo del rango).
const weeklyWeightedPipeline = computed(() =>
  (summary.value?.weighted_pipeline?.series || []).map(point => ({
    label: labelFor(point.timestamp),
    arsWeighted: point.ars,
    usdWeighted: point.usd,
    total: point.ars + point.usd
  }))
);

// Computed - Chart data para pipeline ponderado semanal
const weeklyPipelineChartData = computed(() => {
  const labels = weeklyWeightedPipeline.value.map(week => week.label);
  const arsData = weeklyWeightedPipeline.value.map(week => week.arsWeighted.toFixed(2));
  const usdData = weeklyWeightedPipeline.value.map(week => week.usdWeighted.toFixed(2));

  return {
    labels,
    datasets: [
      {
        label: 'Pipeline Ponderado ARS',
        data: arsData,
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // green-500
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 2
      },
      {
        label: 'Pipeline Ponderado USD',
        data: usdData,
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue-500
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2
      }
    ]
  };
});

// Chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || '';
          const value = parseFloat(context.parsed.y).toFixed(2);
          const currency = label.includes('ARS') ? 'ARS' : 'USD';
          return `${label}: ${new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(value)}`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value) => new Intl.NumberFormat('es-AR').format(value)
      }
    }
  }
};

// Formatear montos
const formattedWeightedPipelineArs = computed(() => 
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(weightedPipeline.value.ars)
);

const formattedWeightedPipelineUsd = computed(() => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(weightedPipeline.value.usd)
);

const onDateRangeChange = (value) => {
  customDateRange.value = value;
};

watch(selectedDateFilter, (newValue) => {
  if (newValue && newValue.id !== DATE_FILTERS.CUSTOM) {
    customDateRange.value = [new Date(), new Date()];
  }
}, { deep: true });
</script>

<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="flex items-start justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('CRM_REPORTS.OBJECTIVES.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {{ $t('CRM_REPORTS.OBJECTIVES.DESCRIPTION') }}
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="multiselect-wrap--small w-48">
            <multiselect
              v-model="selectedDateFilter"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
              :options="dateFilterOptions"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
            />
          </div>
          
          <WootDateRangePicker
            v-if="selectedDateFilter && selectedDateFilter.id === DATE_FILTERS.CUSTOM"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>
      </div>

      <!-- Cards de métricas principales -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ReportMetricCard
          :header="$t('CRM_REPORTS.OBJECTIVES.METRICS.WEIGHTED_PIPELINE_ARS.TITLE')"
          :info-text="$t('CRM_REPORTS.OBJECTIVES.METRICS.WEIGHTED_PIPELINE_ARS.SUBTITLE')"
        >
          <h3 class="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {{ formattedWeightedPipelineArs }}
          </h3>
        </ReportMetricCard>

        <ReportMetricCard
          :header="$t('CRM_REPORTS.OBJECTIVES.METRICS.WEIGHTED_PIPELINE_USD.TITLE')"
          :info-text="$t('CRM_REPORTS.OBJECTIVES.METRICS.WEIGHTED_PIPELINE_USD.SUBTITLE')"
        >
          <h3 class="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {{ formattedWeightedPipelineUsd }}
          </h3>
        </ReportMetricCard>
      </div>

      <!-- Gráfico de Pipeline Ponderado Semanal -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Pipeline Ponderado por Semana
        </h4>
        <div class="h-80">
          <BarChart v-if="weeklyWeightedPipeline.length > 0" :collection="weeklyPipelineChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de deals para el período seleccionado
          </div>
        </div>
      </div>

      <!-- Tabla detallada por semana -->
      <div v-if="weeklyWeightedPipeline.length > 0" class="mt-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Detalle Semanal
        </h4>
        <div class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table class="w-full">
            <thead class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Semana
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Pipeline Ponderado ARS
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Pipeline Ponderado USD
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
              <tr 
                v-for="(week, index) in weeklyWeightedPipeline" 
                :key="index"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                  {{ week.label }}
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                  {{ new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(week.arsWeighted) }}
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                  {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(week.usdWeighted) }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                  {{ new Intl.NumberFormat('es-AR').format(week.total) }}
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Total
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-green-600 dark:text-green-400 text-right tabular-nums">
                  {{ formattedWeightedPipelineArs }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-blue-600 dark:text-blue-400 text-right tabular-nums">
                  {{ formattedWeightedPipelineUsd }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                  {{ new Intl.NumberFormat('es-AR').format(weightedPipeline.total) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.multiselect-wrap--small {
  min-width: 12rem;
}
</style>
